import styled from '@emotion/styled';
import moment from 'moment';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {
  ECancelButton,
  EDeleteButton,
  EForm,
  EFormButtonRow,
  ESubmitButton,
} from 'webapp-lib/Components';
import {mixinFlexboxRow, mixinValidationErrorMessage} from 'webapp-lib/styling';
import {TIME_FORMAT} from 'webapp-lib/utils';

import {CostCenterAutocomplete} from '#/Autocomplete/CostCenterAutocomplete';
import {Dialog} from '#/Dialog/Dialog';
import {useMasterdata} from '_/hooks/useMasterdata';
import {I18NextNS} from '_/i18n';
import {toInternalCostCenterId} from '_/utilities/costCenter';
import {
  EFAutocomplete,
  EFTextArea,
  EFTimeInput,
  requiredAndNonWhitespaceRule,
  requiredRule,
  useForm,
  useWatch,
} from '_/utilities/form';

import {useEventTagColors} from '../hooks/useEventTagColors';
import {EventApiWithWorkLogProps, WorkLog, WorkLogActivity} from '../types';
import {createCostCenterWithDisplayName} from '../utilities/workLog';

import {ColorPickerBox} from './favorites/CreateFavoriteDialog';

enum EditWorkLogeFormFields {
  CostCenterId = 'costCenterId',
  Title = 'title',
  Description = 'description',
  Color = 'color',
  StartTime = 'startTime',
  EndTime = 'endTime',
}

type EditWorkLogFormInputs = {
  [EditWorkLogeFormFields.CostCenterId]: string;
  [EditWorkLogeFormFields.Title]: string;
  [EditWorkLogeFormFields.Description]?: string;
  [EditWorkLogeFormFields.Color]: string;
  [EditWorkLogeFormFields.StartTime]: string;
  [EditWorkLogeFormFields.EndTime]: string;
};

const isValidTimeSlot = (startTime: string, endTime: string): boolean => {
  const start = moment(startTime, TIME_FORMAT);
  const end = moment(endTime, TIME_FORMAT);
  if (!start.isValid() || !end.isValid()) {
    return false;
  }

  const durationHours = moment.duration(end.diff(start)).asHours();
  return durationHours > 0 && durationHours <= 24;
};

export const EditWorkLogDialog = ({
  data,
  isOpen,
  onClose,
  onEdit,
  onDelete,
}: {
  data: EventApiWithWorkLogProps;
  isOpen: boolean;
  onEdit: (event: WorkLog) => void;
  onDelete: (event: EventApiWithWorkLogProps) => void;
  onClose: () => void;
}): React.ReactElement => {
  const {t} = useTranslation<I18NextNS>('timeTracking');
  const masterdata = useMasterdata();
  const {colorAutocompleteOptions} = useEventTagColors();
  const activityData = data.extendedProps.activity;

  const inputCostCenterId =
    activityData?.casusId ??
    toInternalCostCenterId(activityData?.internalHoursCategoryId);

  const {control, formState, clearErrors, getValues, handleSubmit} =
    useForm<EditWorkLogFormInputs>({
      defaultValues: {
        costCenterId: inputCostCenterId || '',
        title: data.title || '',
        description: data.extendedProps?.description || '',
        color: data.backgroundColor,
        startTime: moment(data.start).format(TIME_FORMAT),
        endTime: moment(data.end).format(TIME_FORMAT),
      },
      onSubmit: async ({
        costCenterId,
        color,
        title,
        description,
        startTime,
        endTime,
      }) => {
        let activity: WorkLogActivity | undefined;
        if (costCenterId) {
          activity = await createCostCenterWithDisplayName(
            costCenterId,
            masterdata
          );
        }

        const applyTimeToDate = (date: Date, timeString: string) => {
          const time = moment(timeString, TIME_FORMAT);
          return moment(date)
            .set({hour: time.hour(), minute: time.minute()})
            .toDate();
        };

        const result: WorkLog = {
          id: data.id,
          color,
          title,
          description,
          activity,
          start: applyTimeToDate(data.start!, startTime),
          end: applyTimeToDate(data.end!, endTime),
        };

        onEdit(result);
        onClose();
      },
    });

  const watchedColor = useWatch({
    control,
    name: EditWorkLogeFormFields.Color,
  });
  const clearTimeFieldsErrors = () => {
    clearErrors(EditWorkLogeFormFields.StartTime);
    clearErrors(EditWorkLogeFormFields.EndTime);
  };

  const timeFieldsError =
    formState.errors[EditWorkLogeFormFields.StartTime] ||
    formState.errors[EditWorkLogeFormFields.EndTime];

  const startTimeRules = {
    ...requiredRule,
    validate: (startTime: string) => {
      clearTimeFieldsErrors();

      const {endTime} = getValues();
      return isValidTimeSlot(startTime, endTime);
    },
  };

  const endTimeRules = {
    ...requiredRule,
    validate: (endTime: string) => {
      clearTimeFieldsErrors();

      const {startTime} = getValues();
      return isValidTimeSlot(startTime, endTime);
    },
  };

  return (
    <Dialog
      headerText='Aktivität-Eintrag'
      isShown={isOpen}
      size='tiny'
      onClose={onClose}
    >
      <EForm onSubmit={handleSubmit}>
        <EFTextArea
          control={control}
          label='Titel'
          minRows={2}
          name={EditWorkLogeFormFields.Title}
          rules={requiredAndNonWhitespaceRule}
          autoFocus
        />
        <CostCenterAutocomplete
          control={control}
          internalHoursCategories={masterdata.internalHoursCategories ?? []}
          label='Dossier'
          name={EditWorkLogeFormFields.CostCenterId}
        />
        <EFTextArea
          control={control}
          label='Beschreibung'
          minRows={4}
          name={EditWorkLogeFormFields.Description}
        />
        <ColorPickerBox color={watchedColor}>
          <EFAutocomplete
            control={control}
            label='Farbe'
            name={EditWorkLogeFormFields.Color}
            options={colorAutocompleteOptions}
          />
        </ColorPickerBox>

        <EventTimeRow>
          <EFTimeInput
            control={control}
            label='Start'
            name={EditWorkLogeFormFields.StartTime}
            rules={startTimeRules}
          />
          <span className='dash'>&mdash;</span>
          <EFTimeInput
            control={control}
            label='Ende'
            name={EditWorkLogeFormFields.EndTime}
            rules={endTimeRules}
          />
        </EventTimeRow>
        {timeFieldsError ? (
          <ValidationMessage>
            {t('activity.workLogFormFields.invalid-combination')}
          </ValidationMessage>
        ) : null}

        <EFormButtonRow>
          <ECancelButton disabled={formState.isSubmitting} onClick={onClose} />
          <ButtonRow>
            <EDeleteButton onClick={() => onDelete(data)} />
            <ESubmitButton isSubmitting={formState.isSubmitting} />
          </ButtonRow>
        </EFormButtonRow>
      </EForm>
    </Dialog>
  );
};

export const ValidationMessage = styled.p`
  ${mixinValidationErrorMessage}
`;

const EventTimeRow = styled.div`
  ${mixinFlexboxRow};
  gap: var(--gutter-little);

  .dash {
    height: var(--input-min-height);
    line-height: var(--input-min-height);
    align-self: flex-end;
    vertical-align: middle;
  }
`;

const ButtonRow = styled.span`
  ${mixinFlexboxRow};
  gap: var(--gutter-little);
  justify-content: space-between;
`;
